<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { capitalize, formatNumberDisplayByLang } from '@/helpers/formatters'
import { useUserStore } from '@/stores/user'
import { useEmployeeStore } from '@/stores/employee'
import { storeToRefs } from 'pinia'
import { AdsButton, AdsIcon } from '@accor/ads-components'
import StatusNightCardIcon from '@/assets/icons/status-night-card.svg'
import IconStarCircle from '@/components/atoms/icons/IconStarCircle.vue'
import bellIcon from '@/assets/icons/bell.svg'
import { useRoute, useRouter } from 'vue-router'
import UserCard from '@/components/molecules/UserCard.vue'
import { trackEvent } from '@/services/analytics-service'
import { ACCOUNT_CONTEXT } from '@/constants/gtm4'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const { firstName, productTier, rewardsPoints, balances, hasGuestNotifications, isAab } =
  storeToRefs(useUserStore())
const employeeStore = useEmployeeStore()

const statusPoints = computed(() => balances.value.nbPointsEarnLast12Months)
const currentNights = computed(() => balances.value.currentNightsBalance)

const canSwitchToAab = ref(false)

watch(
  isAab,
  async (value) => {
    if (!value) {
      canSwitchToAab.value = false
      return
    }
    try {
      if (!employeeStore.employee) await employeeStore.setEmployee()
      canSwitchToAab.value = employeeStore.isAuthorizedForAab ?? false
    } catch {
      canSwitchToAab.value = false
    }
  },
  { immediate: true }
)
const switchToAab = () => {
  trackEvent('GTM_AAB_DASHBOARD_NAV_LINK_ACCOUNT_SWITCHER', {
    pageName: route.meta.pageName ?? '',
    blocInteraction: 'switch to professional account',
    context: ACCOUNT_CONTEXT
  })
  router.push({ name: 'aab-dashboard' })
}
</script>

<template>
  <UserCard
    :can-switch="canSwitchToAab"
    :switch-label="t('navigationBar.switchToB2B')"
    @switch="switchToAab"
  >
    <template #top>
      <div class="user-card-b2c__info">
        <div class="user-card-b2c__text">
          <i18n-t keypath="welcome" tag="p" class="user-card-b2c__greeting">
            <template #firstname>
              <span data-cs-mask>{{ capitalize(firstName) }}</span>
            </template>
          </i18n-t>
          <p class="user-card-b2c__status">
            {{ t('navigation.loyalty.tier') }}
            <span
              class="user-card-b2c__tier-value"
              :class="`user-card-b2c__tier-value--${productTier.toLowerCase()}`"
              >{{ productTier }}</span
            >
          </p>
        </div>
        <div class="user-card-b2c__notification">
          <AdsButton
            variant="secondary"
            size="md"
            :aria-label="t('navigation.entry.notifcationCenter')"
            @click="router.push({ name: 'dashboard' })"
            icon-only
            circle
            :prepend-icon="{ icon: bellIcon, size: 'xs' }"
          />
          <span v-if="hasGuestNotifications" class="user-card-b2c__notification-badge" />
        </div>
      </div>
      <div class="user-card-b2c__stat">
        <AdsIcon :icon="StatusNightCardIcon" size="sm" fill="none" />
        <i18n-t keypath="navigation.header.statusPointsAndNights" tag="p">
          <template #statusPoints>
            <span class="user-card-b2c__stat-value">{{
              formatNumberDisplayByLang(statusPoints)
            }}</span>
          </template>
          <template #nights>
            <span class="user-card-b2c__stat-value">{{
              formatNumberDisplayByLang(currentNights)
            }}</span>
          </template>
        </i18n-t>
      </div>
    </template>
    <template #bottom>
      <div class="user-card-b2c__stat">
        <AdsIcon :icon="IconStarCircle" size="sm" fill="none" />
        <i18n-t keypath="navigation.header.rewardPoints" tag="p">
          <template #reward>
            <span class="user-card-b2c__stat-value">{{
              formatNumberDisplayByLang(rewardsPoints)
            }}</span>
          </template>
        </i18n-t>
      </div>
    </template>
  </UserCard>
</template>

<style lang="scss">
@use '@/styles/variables' as *;

.user-card-b2c {
  &__info {
    display: flex;
    flex-direction: row;
    align-self: stretch;
    gap: ads.$ads-spacing-scale-md;
  }

  &__text {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  &__greeting {
    @include ads-sass.title-03;
    color: ads.$ads-primary;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__status {
    @include ads-sass.body-default-02;
    color: ads.$ads-primary;
  }

  &__tier-value {
    @include ads-sass.body-strong-02;

    &--classic {
      color: ads.$ads-classic;
    }
    &--silver {
      color: ads.$ads-silver;
    }
    &--gold {
      color: ads.$ads-gold;
    }
    &--platinum {
      color: ads.$ads-platinum;
    }
    &--diamond {
      color: ads.$ads-diamond;
    }
    &--limitless {
      color: ads.$ads-limitless;
    }
  }

  &__notification {
    position: relative;
    margin-left: auto;
  }

  &__notification-badge {
    position: absolute;
    top: -2px;
    right: 0px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: ads.$ads-on-danger-container-low;
    border: 1px solid ads.$ads-surface;
    pointer-events: none;
  }

  &__stat {
    display: flex;
    flex-direction: row;
    align-items: center;
    align-self: stretch;
    gap: ads.$ads-spacing-scale-xs;
    @include ads-sass.body-default-02;
    color: ads.$ads-primary;
  }

  &__stat-value {
    @include ads-sass.body-strong-02;
  }
}
</style>
